/**
 * Consolidado del mes (ADR 022 · fase 2): todas las cuentas bancarias juntas.
 *
 * 1) Retiros: banco ↔ sistema ↔ contabilidad por cuenta, con lo que explica cada diferencia
 *    (pago doble, banco sin sistema, salió por otra cuenta, sistema sin banco).
 * 2) Conciliación contable: saldo según banco vs saldo según libros por cuenta y sus partidas
 *    conciliatorias (banco no registrado en libros · libros que no pasaron por el banco).
 *
 * Backend: conciliacion_contable_mes (0376) + conciliacion_mes (0374) una vez por cuenta.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import * as XLSX from 'xlsx'
import { supabase } from '@/lib/supabase'
import { formatQ } from '@/lib/format'

type Lado = 'E' | 'S'
type CuentaCont = {
  cuenta: { id: string; banco: string; numero: string; contable: string; activa: boolean }
  banco: { movimientos: number; inicial: number | null; final: number | null; entradas: number; salidas: number; rebotes: number }
  libros: { inicial: number; debe: number; haber: number; final: number }
  retiros: { sistema: number; sistema_n: number; libros: number; libros_n: number; reversas_libros: number }
  cruzados: { banco: number; libros: number }
  banco_no_libros: {
    entradas: number; salidas: number
    por_categoria: { categoria: string; lado: Lado; n: number; monto: number }[]
    detalle: { fecha: string; lado: Lado; categoria: string; monto: number; descripcion: string; contraparte: string | null; doc: string | null }[]
  }
  libros_no_banco: {
    debe: number; haber: number
    detalle: { fecha: string; lado: Lado; monto: number; asiento: string; descripcion: string; origen: string }[]
  }
}
type RetiroBanco = { estado: string; fecha_banco: string; monto: number; beneficiario_banco: string | null; neo_id: string | null; nombre_sistema: string | null; cuenta_salida_sistema: string | null }
type MesCuenta = {
  cargado: { movimientos: number }
  retiros: RetiroBanco[]
  sistema_sin_banco: { neo_id: string; nombre: string; monto: number; fecha_sistema: string; cuenta_salida_sistema: string }[]
}
type Explica = { cuenta: string; tipo: string; fecha: string; monto: number; quien: string; nota: string; signo: 1 | -1 }

const n = (x: unknown) => Number(x ?? 0)
const CAT: Record<string, string> = {
  OTRO_EGRESO: 'Gastos / otros egresos (honorarios, planilla, renta, fondos…)', TRASPASO_PROPIO: 'Traspaso entre cuentas propias',
  INGRESO: 'Depósitos no registrados', RETIRO: 'Retiros no registrados', TRANSFERENCIA_ENVIADA: 'Transferencias enviadas no registradas',
  COMISION: 'Comisiones bancarias', INTERES: 'Intereses ganados', IMPUESTO: 'ISR / impuestos', REVERSA: 'Reversas sin pareja',
}
const NO_SALIDA = new Set(['REBOTADO', 'OTRO_EGRESO'])

export function ConsolidadoMes({ mes }: { mes: string }) {
  const [cont, setCont] = useState<CuentaCont[]>([])
  const [porCuenta, setPorCuenta] = useState<Record<string, MesCuenta>>({})
  const [cargando, setCargando] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [abierto, setAbierto] = useState<string | null>(null)

  const cargar = useCallback(async () => {
    setCargando(true); setErr(null)
    const { data, error } = await supabase.rpc('conciliacion_contable_mes', { p_mes: `${mes}-01` })
    if (error) { setErr(error.message); setCargando(false); return }
    const cuentas = ((data as { cuentas: CuentaCont[] }).cuentas ?? [])
    const pares = await Promise.all(cuentas.map(async (c) => {
      const r = await supabase.rpc('conciliacion_mes', { p_cuenta: c.cuenta.id, p_mes: `${mes}-01` })
      if (r.error) throw new Error(`${c.cuenta.numero}: ${r.error.message}`)
      return [c.cuenta.contable, r.data as MesCuenta] as const
    })).catch((e: Error) => { setErr(e.message); return [] as (readonly [string, MesCuenta])[] })
    setCont(cuentas); setPorCuenta(Object.fromEntries(pares)); setCargando(false)
  }, [mes])

  useEffect(() => { void cargar() }, [cargar])

  const etiqueta = (contable: string | null) => {
    const c = cont.find((x) => x.cuenta.contable === contable)
    return c ? `${c.cuenta.banco} ${c.cuenta.numero}` : (contable ?? '—')
  }

  // ── Retiros: banco ↔ sistema ↔ contabilidad ──────────────────────────────
  const retiros = useMemo(() => cont.map((c) => {
    const m = porCuenta[c.cuenta.contable]
    const salidas = (m?.retiros ?? []).filter((r) => !NO_SALIDA.has(r.estado))
    return {
      cuenta: `${c.cuenta.banco} ${c.cuenta.numero}`, contable: c.cuenta.contable,
      archivo: (m?.cargado.movimientos ?? 0) > 0,
      banco: salidas.reduce((s, r) => s + n(r.monto), 0), banco_n: salidas.length,
      rebotes: (m?.retiros ?? []).filter((r) => r.estado === 'REBOTADO').reduce((s, r) => s + n(r.monto), 0),
      sistema: n(c.retiros.sistema), sistema_n: c.retiros.sistema_n,
      libros: n(c.retiros.libros), libros_n: c.retiros.libros_n,
    }
  }).filter((r) => r.banco || r.sistema || r.libros), [cont, porCuenta])

  const totRet = useMemo(() => retiros.reduce((t, r) => ({
    banco: t.banco + r.banco, rebotes: t.rebotes + r.rebotes, sistema: t.sistema + r.sistema, libros: t.libros + r.libros,
    banco_n: t.banco_n + r.banco_n, sistema_n: t.sistema_n + r.sistema_n, libros_n: t.libros_n + r.libros_n,
  }), { banco: 0, rebotes: 0, sistema: 0, libros: 0, banco_n: 0, sistema_n: 0, libros_n: 0 }), [retiros])

  const explica: Explica[] = useMemo(() => {
    const out: Explica[] = []
    for (const c of cont) {
      const m = porCuenta[c.cuenta.contable]; if (!m) continue
      const cta = `${c.cuenta.banco} ${c.cuenta.numero}`
      for (const r of m.retiros) {
        const quien = r.nombre_sistema ? `${r.neo_id ?? ''} ${r.nombre_sistema}`.trim() : (r.beneficiario_banco ?? 'sin nombre')
        if (r.estado === 'POSIBLE_PAGO_DOBLE') out.push({ cuenta: cta, tipo: 'Posible pago doble', fecha: r.fecha_banco, monto: n(r.monto), quien, nota: 'Salió del banco; el sistema ya tenía ese retiro pagado.', signo: -1 })
        if (r.estado === 'BANCO_SIN_SISTEMA') out.push({ cuenta: cta, tipo: 'Banco sin retiro en sistema', fecha: r.fecha_banco, monto: n(r.monto), quien, nota: 'Salida a asociado sin retiro acreditado que la respalde.', signo: -1 })
        if (r.estado === 'CUADRA_OTRA_CUENTA') out.push({ cuenta: cta, tipo: 'Salió por otra cuenta', fecha: r.fecha_banco, monto: n(r.monto), quien, nota: `Sistema y contabilidad lo registraron en ${etiqueta(r.cuenta_salida_sistema)}.`, signo: -1 })
      }
      if (m.cargado.movimientos === 0 && n(c.retiros.sistema) > 0) {
        out.push({ cuenta: cta, tipo: 'Cuenta sin estado de cuenta cargado', fecha: mes, monto: n(c.retiros.sistema),
          quien: `${c.retiros.sistema_n} retiro(s) registrados con esta cuenta`, nota: 'Si salieron por otra cuenta, se compensan con "Salió por otra cuenta". Si no, subí el estado de cuenta.', signo: 1 })
      }
      for (const s of m.sistema_sin_banco) {
        const otra = cont.some((x) => x.cuenta.contable !== c.cuenta.contable && (porCuenta[x.cuenta.contable]?.retiros ?? [])
          .some((r) => r.estado === 'CUADRA_OTRA_CUENTA' && r.neo_id === s.neo_id && n(r.monto) === n(s.monto)))
        out.push({ cuenta: cta, tipo: otra ? 'Registrado aquí, salió por otra cuenta' : 'Sistema sin salida en banco', fecha: s.fecha_sistema, monto: n(s.monto),
          quien: `${s.neo_id} ${s.nombre}`, nota: otra ? 'Se compensa con "Salió por otra cuenta".' : (m.cargado.movimientos ? 'Retiro acreditado sin débito en el estado de cuenta.' : 'No hay estado de cuenta cargado para esta cuenta.'), signo: 1 })
      }
    }
    return out
  }, [cont, porCuenta, mes]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Contable: banco vs libros ────────────────────────────────────────────
  const contable = useMemo(() => cont.map((c) => {
    const bi = c.banco.inicial == null ? null : n(c.banco.inicial)
    const bf = c.banco.final == null ? null : n(c.banco.final)
    const arrastre = bi == null ? null : bi - n(c.libros.inicial)
    const bnl = n(c.banco_no_libros.entradas) - n(c.banco_no_libros.salidas)
    const lnb = n(c.libros_no_banco.debe) - n(c.libros_no_banco.haber)
    const dif = bf == null ? null : bf - n(c.libros.final)
    const residual = dif == null || arrastre == null ? null : Math.round((dif - (arrastre + bnl - lnb)) * 100) / 100
    return { c, bi, bf, arrastre, bnl, lnb, dif, residual }
  }), [cont])

  const exportar = () => {
    const wb = XLSX.utils.book_new()
    const hoja = (rows: object[], nombre: string) =>
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.length ? rows : [{ sin: 'sin datos' }]), nombre)
    hoja([...retiros.map((r) => ({
      cuenta: r.cuenta, cuenta_contable: r.contable, banco_salidas: r.banco, banco_n: r.banco_n, rebotes_neutralizados: r.rebotes,
      sistema: r.sistema, sistema_n: r.sistema_n, contabilidad: r.libros, contabilidad_n: r.libros_n,
      dif_banco_sistema: r.banco - r.sistema, dif_sistema_contabilidad: r.sistema - r.libros,
    })), { cuenta: 'TOTAL', banco_salidas: totRet.banco, banco_n: totRet.banco_n, rebotes_neutralizados: totRet.rebotes, sistema: totRet.sistema,
      sistema_n: totRet.sistema_n, contabilidad: totRet.libros, contabilidad_n: totRet.libros_n,
      dif_banco_sistema: totRet.banco - totRet.sistema, dif_sistema_contabilidad: totRet.sistema - totRet.libros }], 'Retiros por cuenta')
    hoja(explica.map((e) => ({ cuenta: e.cuenta, tipo: e.tipo, fecha: e.fecha, monto: e.monto, efecto_banco_a_sistema: e.signo * e.monto, asociado: e.quien, nota: e.nota })), 'Explicación retiros')
    hoja(contable.map(({ c, bi, bf, arrastre, bnl, lnb, dif, residual }) => ({
      cuenta: `${c.cuenta.banco} ${c.cuenta.numero}`, cuenta_contable: c.cuenta.contable,
      banco_saldo_inicial: bi, banco_saldo_final: bf, libros_saldo_inicial: n(c.libros.inicial), libros_debe_mes: n(c.libros.debe),
      libros_haber_mes: n(c.libros.haber), libros_saldo_final: n(c.libros.final), diferencia_inicial: arrastre,
      banco_no_en_libros_neto: bnl, libros_no_en_banco_neto: lnb, diferencia_final: dif, residual_sin_explicar: residual,
    })), 'Contable resumen')
    hoja(cont.flatMap((c) => c.banco_no_libros.por_categoria.map((p) => ({
      cuenta: `${c.cuenta.banco} ${c.cuenta.numero}`, categoria: CAT[p.categoria] ?? p.categoria, lado: p.lado === 'E' ? 'Entrada' : 'Salida', movimientos: p.n, monto: n(p.monto),
    }))), 'Banco no en libros (resumen)')
    hoja(cont.flatMap((c) => c.banco_no_libros.detalle.map((d) => ({
      cuenta: `${c.cuenta.banco} ${c.cuenta.numero}`, fecha: d.fecha, lado: d.lado === 'E' ? 'Entrada' : 'Salida', categoria: CAT[d.categoria] ?? d.categoria,
      monto: n(d.monto), descripcion: d.descripcion, contraparte: d.contraparte, doc: d.doc,
    }))), 'Banco no en libros')
    hoja(cont.flatMap((c) => c.libros_no_banco.detalle.map((d) => ({
      cuenta: `${c.cuenta.banco} ${c.cuenta.numero}`, fecha: d.fecha, lado: d.lado === 'E' ? 'Debe' : 'Haber', monto: n(d.monto),
      asiento: d.asiento, descripcion: d.descripcion, origen: d.origen,
    }))), 'Libros no en banco')
    XLSX.writeFile(wb, `conciliacion_consolidada_${mes}.xlsx`)
  }

  const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: 11, color: 'var(--muted-foreground)', fontWeight: 600, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }
  const thr: React.CSSProperties = { ...th, textAlign: 'right' }
  const td: React.CSSProperties = { padding: '7px 10px', fontSize: 12.5, borderBottom: '1px solid var(--border)', verticalAlign: 'top' }
  const num: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }
  const card: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 12, padding: 14, background: 'var(--card, #fff)' }
  const difStyle = (v: number | null): React.CSSProperties => ({ ...num, fontWeight: 600, color: v == null ? undefined : Math.abs(v) < 0.005 ? '#047857' : '#b91c1c' })
  const q = (v: number | null) => (v == null ? '—' : formatQ(v))
  const signed = (v: number) => `${v >= 0 ? '+' : '−'}${formatQ(Math.abs(v))}`

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button onClick={exportar} disabled={cargando || !cont.length}
          style={{ padding: '8px 14px', borderRadius: 8, background: 'transparent', border: '1px solid var(--border)', fontWeight: 600, cursor: 'pointer' }}>
          Exportar consolidado a Excel
        </button>
        {cargando && <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>Cruzando todas las cuentas…</span>}
      </div>
      {err && <div style={{ ...card, background: '#fef2f2', color: '#991b1b', fontSize: 13 }}>{err}</div>}
      {!cargando && !err && cont.length === 0 && <div style={{ ...card, fontSize: 13 }}>Sin movimientos de banco ni partidas contables para {mes}.</div>}

      {retiros.length > 0 && (
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>1 · Retiros del mes: banco ↔ sistema ↔ contabilidad</div>
          <p style={{ fontSize: 12, color: 'var(--muted-foreground)', margin: '4px 0 10px' }}>
            Banco = salidas a asociados según el estado de cuenta, sin rebotes. Sistema = retiros acreditados por cuenta de salida. Contabilidad = HABER de los asientos de retiro en la cuenta del banco.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Cuenta</th><th style={thr}>Banco</th><th style={thr}>Rebotes (neutros)</th><th style={thr}>Sistema</th><th style={thr}>Contabilidad</th><th style={thr}>Banco − Sistema</th><th style={thr}>Sistema − Conta</th></tr></thead>
              <tbody>
                {retiros.map((r) => (
                  <tr key={r.contable}>
                    <td style={td}>{r.cuenta}<div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{r.contable}{!r.archivo && ' · sin estado de cuenta cargado'}</div></td>
                    <td style={num}>{formatQ(r.banco)}<div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{r.banco_n}</div></td>
                    <td style={num}>{r.rebotes ? formatQ(r.rebotes) : '—'}</td>
                    <td style={num}>{formatQ(r.sistema)}<div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{r.sistema_n}</div></td>
                    <td style={num}>{formatQ(r.libros)}<div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{r.libros_n}</div></td>
                    <td style={difStyle(r.banco - r.sistema)}>{signed(r.banco - r.sistema)}</td>
                    <td style={difStyle(r.sistema - r.libros)}>{signed(r.sistema - r.libros)}</td>
                  </tr>
                ))}
                <tr style={{ background: 'var(--muted, #f4f4f5)' }}>
                  <td style={{ ...td, fontWeight: 700 }}>Total del mes</td>
                  <td style={{ ...num, fontWeight: 700 }}>{formatQ(totRet.banco)}</td><td style={num}>{totRet.rebotes ? formatQ(totRet.rebotes) : '—'}</td>
                  <td style={{ ...num, fontWeight: 700 }}>{formatQ(totRet.sistema)}</td><td style={{ ...num, fontWeight: 700 }}>{formatQ(totRet.libros)}</td>
                  <td style={difStyle(totRet.banco - totRet.sistema)}>{signed(totRet.banco - totRet.sistema)}</td>
                  <td style={difStyle(totRet.sistema - totRet.libros)}>{signed(totRet.sistema - totRet.libros)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {explica.length > 0 && (
            <>
              <div style={{ fontWeight: 600, fontSize: 13, margin: '14px 0 6px' }}>Qué explica la diferencia banco − sistema</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={th}>Cuenta</th><th style={th}>Tipo</th><th style={th}>Fecha</th><th style={th}>Asociado / beneficiario</th><th style={thr}>Efecto</th><th style={th}>Nota</th></tr></thead>
                  <tbody>{explica.map((e, i) => (
                    <tr key={i}><td style={td}>{e.cuenta}</td><td style={td}>{e.tipo}</td><td style={td}>{e.fecha}</td><td style={td}>{e.quien}</td>
                      <td style={{ ...num, color: e.signo < 0 ? '#b91c1c' : '#047857' }}>{signed(e.signo * e.monto)}</td><td style={{ ...td, fontSize: 12, color: 'var(--muted-foreground)' }}>{e.nota}</td></tr>))}
                    <tr><td style={{ ...td, fontWeight: 700 }} colSpan={4}>Banco + efectos = Sistema</td>
                      <td style={{ ...num, fontWeight: 700 }}>{formatQ(totRet.banco + explica.reduce((s, e) => s + e.signo * e.monto, 0))}</td>
                      <td style={{ ...td, fontSize: 12 }}>{Math.abs(totRet.banco + explica.reduce((s, e) => s + e.signo * e.monto, 0) - totRet.sistema) < 0.005 ? '✓ cuadra con sistema' : `queda ${signed(totRet.sistema - totRet.banco - explica.reduce((s, e) => s + e.signo * e.monto, 0))} sin explicar`}</td></tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {contable.length > 0 && (
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>2 · Conciliación contable: saldo según banco vs saldo según libros</div>
          <p style={{ fontSize: 12, color: 'var(--muted-foreground)', margin: '4px 0 10px' }}>
            Libros = partidas de la cuenta contable del banco (asientos no anulados). Diferencia final = diferencia al inicio + banco no registrado en libros − libros que no pasaron por el banco.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {contable.map(({ c, bi, bf, arrastre, bnl, lnb, dif, residual }) => {
              const k = c.cuenta.contable
              return (
                <div key={k} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ fontWeight: 700 }}>{c.cuenta.banco} {c.cuenta.numero} <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--muted-foreground)' }}>· {k}{c.cuenta.activa ? '' : ' · inactiva'}</span></div>
                    <div style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>{c.banco.movimientos} mov. banco · {c.cruzados.banco} cruzados con {c.cruzados.libros} partidas</div>
                  </div>
                  {c.banco.movimientos === 0 && <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>No hay estado de cuenta cargado para este mes: todo lo de libros aparece como “no pasó por el banco”.</div>}
                  {c.banco.movimientos > 0 && bf == null && <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>El archivo cargado no trae saldos (la “Consulta de Transferencias” de BAC solo lista transferencias en línea; no trae depósitos de otros bancos ni saldo). Para cuadrar saldo, subí el estado de cuenta completo.</div>}
                  <table style={{ borderCollapse: 'collapse', marginTop: 8, minWidth: 520 }}>
                    <tbody>
                      <tr><td style={td}>Saldo según banco (inicio → cierre)</td><td style={num}>{q(bi)}</td><td style={num}>{q(bf)}</td></tr>
                      <tr><td style={td}>Saldo según libros (inicio → cierre)</td><td style={num}>{formatQ(n(c.libros.inicial))}</td><td style={num}>{formatQ(n(c.libros.final))}</td></tr>
                      <tr><td style={td}>Diferencia al inicio del mes (arrastre)</td><td style={num} /><td style={num}>{arrastre == null ? '—' : signed(arrastre)}</td></tr>
                      <tr><td style={td}>+ Banco no registrado en libros (entradas − salidas)</td><td style={num} /><td style={num}>{signed(bnl)}</td></tr>
                      <tr><td style={td}>− Libros que no pasaron por el banco (debe − haber)</td><td style={num} /><td style={num}>{signed(-lnb)}</td></tr>
                      <tr><td style={{ ...td, fontWeight: 700 }}>Diferencia al cierre (banco − libros)</td><td style={num} /><td style={difStyle(dif)}>{dif == null ? '—' : signed(dif)}</td></tr>
                      {residual != null && <tr><td style={{ ...td, fontSize: 11, color: 'var(--muted-foreground)' }}>Control</td><td style={num} /><td style={{ ...num, fontSize: 11, color: residual === 0 ? '#047857' : '#b91c1c' }}>{residual === 0 ? '✓ explicada al centavo' : `residual ${signed(residual)}`}</td></tr>}
                    </tbody>
                  </table>
                  {c.banco_no_libros.por_categoria.length > 0 && (
                    <table style={{ borderCollapse: 'collapse', marginTop: 10, minWidth: 520 }}>
                      <thead><tr><th style={th}>Banco no en libros · categoría</th><th style={th}>Lado</th><th style={thr}>Mov.</th><th style={thr}>Monto</th></tr></thead>
                      <tbody>{c.banco_no_libros.por_categoria.map((p, i) => (
                        <tr key={i}><td style={td}>{CAT[p.categoria] ?? p.categoria}</td><td style={td}>{p.lado === 'E' ? 'Entrada' : 'Salida'}</td><td style={num}>{p.n}</td>
                          <td style={{ ...num, color: p.lado === 'E' ? '#047857' : '#b91c1c' }}>{p.lado === 'E' ? '+' : '−'}{formatQ(n(p.monto))}</td></tr>))}
                        {(n(c.libros_no_banco.debe) > 0 || n(c.libros_no_banco.haber) > 0) && (
                          <tr><td style={td} colSpan={2}>Libros no en banco · debe / haber</td><td style={num}>{c.libros_no_banco.detalle.length}</td>
                            <td style={num}>{formatQ(n(c.libros_no_banco.debe))} / {formatQ(n(c.libros_no_banco.haber))}</td></tr>)}
                      </tbody>
                    </table>
                  )}
                  <button onClick={() => setAbierto(abierto === k ? null : k)}
                    style={{ marginTop: 8, border: 0, background: 'transparent', fontSize: 12, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>
                    {abierto === k ? 'Ocultar detalle' : 'Ver detalle línea a línea'}
                  </button>
                  {abierto === k && (
                    <div style={{ display: 'grid', gap: 10, marginTop: 8, overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr><th style={th} colSpan={6}>Banco no registrado en libros (sin comisiones; esas van agrupadas arriba)</th></tr>
                          <tr><th style={th}>Fecha</th><th style={th}>Categoría</th><th style={th}>Descripción</th><th style={th}>Contraparte</th><th style={th}>Doc</th><th style={thr}>Monto</th></tr></thead>
                        <tbody>{c.banco_no_libros.detalle.map((d, i) => (
                          <tr key={i}><td style={td}>{d.fecha}</td><td style={td}>{CAT[d.categoria] ?? d.categoria}</td><td style={td}>{d.descripcion}</td><td style={td}>{d.contraparte ?? '—'}</td><td style={td}>{d.doc ?? '—'}</td>
                            <td style={{ ...num, color: d.lado === 'E' ? '#047857' : '#b91c1c' }}>{d.lado === 'E' ? '+' : '−'}{formatQ(n(d.monto))}</td></tr>))}</tbody>
                      </table>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr><th style={th} colSpan={5}>Libros que no pasaron por el banco</th></tr>
                          <tr><th style={th}>Fecha</th><th style={th}>Asiento</th><th style={th}>Descripción</th><th style={th}>Origen</th><th style={thr}>Debe / Haber</th></tr></thead>
                        <tbody>{c.libros_no_banco.detalle.map((d, i) => (
                          <tr key={i}><td style={td}>{d.fecha}</td><td style={td}>{d.asiento}</td><td style={td}>{d.descripcion}</td><td style={td}>{d.origen}</td>
                            <td style={num}>{d.lado === 'E' ? `D ${formatQ(n(d.monto))}` : `H ${formatQ(n(d.monto))}`}</td></tr>))}</tbody>
                      </table>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
